import React, { useState } from 'react';
import './ContactOverlay.css';
import TermsOverlay from '../terms/TermsOverlay';

interface ContactOverlayProps {
    onClose: () => void;
}

export default function ContactOverlay({ onClose }: ContactOverlayProps) {
    const [formData, setFormData] = useState({
        companyName: '',
        contactName: '',
        phone: '',
        email: '',
        fleetSize: '1~10대',
        message: ''
    });

    const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
    const [termsModal, setTermsModal] = useState<'service' | 'location' | 'privacy' | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        // Simple phone formatting
        if (name === 'phone') {
            const numbers = value.replace(/[^\d]/g, '').slice(0, 11);
            let formatted = numbers;
            if (numbers.length > 3 && numbers.length <= 7) {
                formatted = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
            } else if (numbers.length > 7) {
                formatted = `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
            }
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('submitting');
        // Simulate API call
        setTimeout(() => {
            setStatus('success');
            setTimeout(() => {
                onClose();
            }, 2500);
        }, 1200);
    };

    return (
        <div className="contact-overlay">
            <div className="contact-container">
                {status === 'success' ? (
                    <div className="contact-success">
                        <div className="contact-success-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <h3 className="contact-success-title">문의 접수 완료</h3>
                        <p className="contact-success-desc">
                            성공적으로 문의가 접수되었습니다.<br/>
                            담당자가 확인 후 빠른 시일 내에 연락드리겠습니다.
                        </p>
                    </div>
                ) : (
                    <>
                        <button className="contact-close-btn" onClick={onClose}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                        <div className="contact-header">
                            <h2 className="contact-title">엔터프라이즈 도입 문의</h2>
                            <p className="contact-subtitle">
                                비즈니스 규모와 목적에 맞는 최적의 맞춤형 솔루션을 제안해 드립니다.
                            </p>
                        </div>

                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="contact-field-row">
                                <div className="contact-field">
                                    <label>회사명 <span className="req">*</span></label>
                                    <input type="text" name="companyName" placeholder="주식회사 EView" required value={formData.companyName} onChange={handleChange} />
                                </div>
                                <div className="contact-field">
                                    <label>담당자 성함 <span className="req">*</span></label>
                                    <input type="text" name="contactName" placeholder="홍길동" required value={formData.contactName} onChange={handleChange} />
                                </div>
                            </div>

                            <div className="contact-field-row">
                                <div className="contact-field">
                                    <label>연락처 <span className="req">*</span></label>
                                    <input type="tel" name="phone" placeholder="010-0000-0000" required value={formData.phone} onChange={handleChange} />
                                </div>
                                <div className="contact-field">
                                    <label>회사 이메일 <span className="req">*</span></label>
                                    <input type="email" name="email" placeholder="example@company.com" required value={formData.email} onChange={handleChange} />
                                </div>
                            </div>

                            <div className="contact-field">
                                <label>예상 도입 차량 대수 <span className="req">*</span></label>
                                <select name="fleetSize" value={formData.fleetSize} onChange={handleChange} required>
                                    <option value="1~10대">1 ~ 10대</option>
                                    <option value="11~50대">11 ~ 50대</option>
                                    <option value="51~100대">51 ~ 100대</option>
                                    <option value="100대 이상">100대 이상 (대규모 플릿)</option>
                                    <option value="아직 모름">아직 모름 (상담 필요)</option>
                                </select>
                            </div>

                            <div className="contact-field">
                                <label>문의 내용 <span className="req">*</span></label>
                                <textarea name="message" placeholder="필요하신 기능이나 현재 관리 중인 차량 환경(충전 인프라 여부 등)을 간략하게 적어주시면 더 정확한 상담이 가능합니다." rows={4} required value={formData.message} onChange={handleChange}></textarea>
                            </div>

                            <div className="contact-footer">
                                <button type="submit" className={`contact-submit-btn ${status === 'submitting' ? 'loading' : ''}`} disabled={status === 'submitting'}>
                                    {status === 'submitting' ? (
                                        <div className="spinner"></div>
                                    ) : '도입 문의하기'}
                                </button>
                                <p className="contact-terms-notice">
                                    문의를 남기시면 <a href="#" onClick={(e) => { e.preventDefault(); setTermsModal('privacy'); }}>개인정보 수집 및 이용 ↗</a>에 동의한 것으로 간주됩니다.
                                </p>
                            </div>
                        </form>
                    </>
                )}
            </div>
            {termsModal && (
                <TermsOverlay type={termsModal} onClose={() => setTermsModal(null)} />
            )}
        </div>
    );
}
